/**
 * B93 slice 4 — the billing page's ONE read (spec §10.5's meter, read
 * back): what model this tenant is on, what the current utc month has
 * accrued so far, and every invoice the monthly cadence has drafted.
 * ADMIN-ONLY audience: what the platform charges the tenant is
 * administration, and administration is deny-by-default. Scope rides
 * ledger:read per the B80 no-new-vocabulary precedent — the read is
 * books-adjacent (it reads the platform_fees meter and its drafted
 * invoices), and a dedicated billing:read scope would be an authorizer
 * amendment (escalation). CAPABILITY stays walled: the CAPABILITY-opened
 * set remains the four report ops.
 *
 * ONE read by design (B152/B159/B162's cost discipline applied at the
 * contract): the page needs plan + usage + history together, every time —
 * three ops would be three round-trips of the same story. No id renders
 * from this shape: invoices are named by their PERIOD (R7 — the
 * deterministic invoiceId is derivable server-side and stays off the wire).
 */
import { z } from "zod";
import { readOp, zEpochMs, zMoney } from "./common.js";

const zBillingModel = z
  .object({
    subscription: z.object({ tierName: z.string().min(1), monthlyFee: zMoney }).strict().optional(),
    /** Bill the period's accrued platform_fees — the per-ticket meter. */
    meteredPlatformFees: z.boolean(),
    /** B410 (#19) — the agreed per-ticket rate, named by the plan card; absent stays honest. */
    perTicketFee: zMoney.optional(),
  })
  .strict();

const zInvoiceLine = z
  .object({
    kind: z.enum(["subscription", "metered_platform_fees"]),
    description: z.string().min(1),
    amount: zMoney,
    /** Metered lines: how many journals contributed (an honest zero is a zero). */
    journalCount: z.number().int().nonnegative().optional(),
  })
  .strict();

/**
 * B93 collection leg — whether the platform is actually chasing this
 * invoice, and where the tenant pays it.
 *
 * ABSENT means nobody is collecting: no default is shipped and the
 * sweeper holds no Stripe custody today, so absence is every real
 * tenant's honest state and must never be read as "paid".
 *
 * `hostedInvoiceUrl` is the ONE provider-supplied string on this wire and
 * the page puts it in an href, so its SCHEME is walled here rather than
 * trusted: `z.string().url()` alone would pass `javascript:alert(1)`,
 * which is a valid URL and an XSS vector the moment it reaches an anchor.
 */
const zHostedInvoiceUrl = z
  .string()
  .url()
  // A ZodString CHECK, deliberately, not a .refine(): refinements are
  // ZodEffects, which the B22 MCP membrane refuses to translate — and
  // this way the scheme wall also lands in the generated tool schema as a
  // `pattern`, instead of living only in our own parse.
  .regex(/^https:\/\//i, "hosted invoice link must be https");

const zInvoiceCollection = z
  .object({
    /** The lifecycle's own word; the page translates it (R9). */
    status: z.enum(["awaiting_payment", "past_due", "paid", "uncollectible", "voided"]),
    /** When it falls (or fell) due — the dunning clock's one visible fact. */
    dueAtMs: zEpochMs,
    /** B411 (b), defaulted 2026-08-28: when the provider said paid — absent
     *  until then (and on records paid before the field existed). */
    paidAtMs: zEpochMs.optional(),
    /** Absent when the provider issued no payable page. */
    hostedInvoiceUrl: zHostedInvoiceUrl.optional(),
  })
  .strict();

const zInvoiceRow = z
  .object({
    periodStartMs: zEpochMs,
    periodEndMs: zEpochMs,
    status: z.enum(["draft"]),
    /** True when the period produced nothing to collect. */
    nothingDue: z.boolean(),
    total: zMoney,
    generatedAt: zEpochMs,
    lines: z.array(zInvoiceLine).min(1),
    /** Absent = nobody is collecting this one. Never a guessed "paid". */
    collection: zInvoiceCollection.optional(),
  })
  .strict();

export const billingOverview = readOp({
  name: "billing.overview",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      /** No default shipped: absence answers enabled=false WITH the reason in words. */
      invoicing: z.discriminatedUnion("enabled", [
        z.object({ enabled: z.literal(false), reason: z.string().min(1) }).strict(),
        z.object({ enabled: z.literal(true), model: zBillingModel }).strict(),
      ]),
      /** The current utc month so far — per-currency, never a one-currency guess. */
      usage: z
        .object({
          periodStartMs: zEpochMs,
          periodEndMs: zEpochMs,
          journalCount: z.number().int().nonnegative(),
          fees: z.array(zMoney),
        })
        .strict(),
      /** Newest period first — display order is wire order. */
      invoices: z.array(zInvoiceRow),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    invoicing: {
      enabled: true,
      model: { subscription: { tierName: "Standard", monthlyFee: { amount: 9_900, currency: "USD" } }, meteredPlatformFees: true },
    },
    usage: {
      periodStartMs: 1_782_000_000_000,
      periodEndMs: 1_782_800_000_000,
      journalCount: 2,
      fees: [{ amount: 200, currency: "USD" }],
    },
    invoices: [
      {
        periodStartMs: 1_779_400_000_000,
        periodEndMs: 1_782_000_000_000,
        status: "draft",
        nothingDue: false,
        total: { amount: 10_899, currency: "USD" },
        generatedAt: 1_782_000_000_000,
        lines: [
          { kind: "subscription", description: "Standard", amount: { amount: 9_900, currency: "USD" } },
          { kind: "metered_platform_fees", description: "Per-ticket platform fees", amount: { amount: 999, currency: "USD" }, journalCount: 3 },
        ],
      },
    ],
  },
});
